import { App } from "@capacitor/app";
import { AppLauncher } from "@capacitor/app-launcher";
import { Capacitor } from "@capacitor/core";
import { Drawer, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TimeTabIcon,
  TransformTabIcon,
} from "../Admin/Pages/DownloadApp/AppIcons";
import { allCommonImages } from "../Assets/Images/images";
import {
  DEFAULT_APP_PACKAGE,
  getAppPackageName,
  TIME_VARIANT_PACKAGE,
} from "./appPackage";

let logo = allCommonImages.LogoWhite;

const CUSTOM_SCHEME = "transform";
const DEEP_LINK_HOSTS = new Set(["app.sperrinmetal.com", "dev.3rddigital.com"]);
const APP_HANDOFF_PARAM = "__smcrm_app_handoff";
const PROCESSED_LAUNCH_URL_KEY_PREFIX = "smcrm-processed-launch-url:";

const getLaunchUrlFingerprint = (url: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < url.length; index++) {
    hash = Math.imul(hash ^ url.charCodeAt(index), 16777619);
  }
  return `${url.length}:${(hash >>> 0).toString(36)}`;
};

let ALLOWED_HOST = "";
try {
  const apiUrl = new URL(process.env.REACT_APP_API_URL || "");
  ALLOWED_HOST = apiUrl.hostname;
} catch (e) {}

const DeepLinkingHandler = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [showFallbackPopup, setShowFallbackPopup] = useState(false);
  const [showAppChooser, setShowAppChooser] = useState(false);
  const [deepLinkPath, setDeepLinkPath] = useState("");
  const [currentApp, setCurrentApp] = useState<"transform" | "time">(
    "transform",
  );

  useEffect(() => {
    let isMounted = true;
    let appUrlListener: Awaited<ReturnType<typeof App.addListener>> | null =
      null;
    const markLaunchUrlProcessed = (url: string): boolean => {
      const key = `${PROCESSED_LAUNCH_URL_KEY_PREFIX}${getLaunchUrlFingerprint(
        url,
      )}`;
      try {
        if (window.sessionStorage.getItem(key) === "1") {
          return true;
        }
        window.sessionStorage.setItem(key, "1");
      } catch (error) {
        console.error(
          "[DeepLinkingHandler] Failed to track the processed launch URL:",
          error,
        );
      }
      return false;
    };

    const handleIncomingUrl = async (url: string) => {
      try {
        const incomingUrl = new URL(url);
        const incomingScheme = incomingUrl.protocol.slice(0, -1);
        const incomingAppTarget =
          incomingScheme === "time" || incomingScheme === TIME_VARIANT_PACKAGE
            ? "time"
            : incomingScheme === CUSTOM_SCHEME ||
              incomingScheme === DEFAULT_APP_PACKAGE
            ? "transform"
            : null;
        const isCustomScheme = [
          CUSTOM_SCHEME,
          "time",
          DEFAULT_APP_PACKAGE,
          TIME_VARIANT_PACKAGE,
        ].includes(incomingScheme);

        if (!isCustomScheme) {
          const incomingHost = incomingUrl.hostname;
          if (
            !DEEP_LINK_HOSTS.has(incomingHost) &&
            incomingHost !== ALLOWED_HOST
          ) {
            return;
          }
        }

        let incomingPath = "";
        if (isCustomScheme) {
          const hostPart =
            incomingUrl.hostname && incomingUrl.hostname !== CUSTOM_SCHEME
              ? `/${incomingUrl.hostname}`
              : "";
          incomingPath = `${hostPart}${incomingUrl.pathname}`;
        } else {
          incomingPath = incomingUrl.pathname;
        }

        const routePath =
          incomingPath.replace(/^\/(?:sm-crm|smcrm-live)(?=\/|$)/, "") || "/";
        const routeSearchParams = new URLSearchParams(incomingUrl.search);
        const handoffTarget = routeSearchParams.get(APP_HANDOFF_PARAM);
        routeSearchParams.delete(APP_HANDOFF_PARAM);
        const routeSearch = routeSearchParams.toString();
        const fullPath = `${routePath.startsWith("/") ? "" : "/"}${routePath}${
          routeSearch ? `?${routeSearch}` : ""
        }${incomingUrl.hash}`;

        if (!fullPath) {
          return;
        }

        const navigateToLink = () => {
          window.setTimeout(() => {
            if (isMounted) {
              navigateRef.current(fullPath);
            }
          }, 500);
        };

        const platform = Capacitor.getPlatform();
        if (
          !Capacitor.isNativePlatform() ||
          (platform !== "ios" && platform !== "android")
        ) {
          navigateToLink();
          return;
        }

        let packageName: string;
        try {
          packageName = await getAppPackageName();
          const transformScheme =
            platform === "ios" ? "transform" : DEFAULT_APP_PACKAGE;
          const timeScheme = platform === "ios" ? "time" : TIME_VARIANT_PACKAGE;
          const [transformApp, timeApp] = await Promise.all([
            AppLauncher.canOpenUrl({ url: `${transformScheme}://` }),
            AppLauncher.canOpenUrl({ url: `${timeScheme}://` }),
          ]);

          if (isMounted && transformApp.value && timeApp.value) {
            const current: "transform" | "time" =
              packageName === TIME_VARIANT_PACKAGE ? "time" : "transform";
            setCurrentApp(current);
            const wasExplicitlyOpenedInCurrentApp =
              incomingAppTarget === current;
            if (handoffTarget !== current && !wasExplicitlyOpenedInCurrentApp) {
              setDeepLinkPath(fullPath);
              setShowAppChooser(true);
              return;
            }
          }
        } catch (error) {
          console.error(
            "[DeepLinkingHandler] Failed to check installed app variants:",
            error,
          );
        }

        navigateToLink();
      } catch (error) {
        console.error(
          "[DeepLinkingHandler] Failed to process app link:",
          error,
        );
      }
    };

    const setupAppUrlListener = async () => {
      appUrlListener = await App.addListener("appUrlOpen", ({ url }) => {
        markLaunchUrlProcessed(url);
        void handleIncomingUrl(url);
      });

      if (!isMounted) {
        await appUrlListener.remove();
        return;
      }

      if (Capacitor.isNativePlatform()) {
        const launchUrl = await App.getLaunchUrl();
        if (launchUrl?.url) {
          if (markLaunchUrlProcessed(launchUrl.url)) {
            return;
          }

          await handleIncomingUrl(launchUrl.url);
        }
      }
    };

    void setupAppUrlListener().catch((error) => {
      console.error(
        "[DeepLinkingHandler] Failed to initialize app link handling:",
        error,
      );
    });

    return () => {
      isMounted = false;
      if (appUrlListener) {
        void appUrlListener.remove().catch((error) => {
          console.error(
            "[DeepLinkingHandler] Failed to remove app link listener:",
            error,
          );
        });
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const userAgent = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    const isSafariBrowser =
      /Safari/.test(userAgent) &&
      !/Chrome|CriOS|FxiOS|OPiOS|DuckDuckGo/.test(userAgent);
    const isNativeApp = Capacitor.isNativePlatform();

    if (!isIOSDevice || isSafariBrowser || isNativeApp) {
      return;
    }

    const alreadyShown = window.sessionStorage.getItem(
      "deep-link-fallback-shown",
    );
    if (alreadyShown) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setShowFallbackPopup(true);
      window.sessionStorage.setItem("deep-link-fallback-shown", "true");
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, []);

  const handleOpenBrowserApp = (app: "transform" | "time") => {
    if (typeof window === "undefined") {
      return;
    }
    const originalPath =
      sessionStorage.getItem("original-deep-link") ||
      `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const linkUrl = new URL(originalPath, window.location.origin);
    const routePath =
      linkUrl.pathname.replace(/^\/(?:sm-crm|smcrm-live)(?=\/|$)/, "") || "/";
    const appUrl = `${app}://${routePath}${linkUrl.search}${linkUrl.hash}`;
    setShowFallbackPopup(false);
    window.location.href = appUrl;
    setTimeout(() => {
      sessionStorage.removeItem("original-deep-link");
    }, 2000);
  };

  const openInApp = async (app: "transform" | "time") => {
    setShowAppChooser(false);
    if (app === currentApp) {
      navigate(deepLinkPath);
      return;
    }

    const platform = Capacitor.getPlatform();
    const scheme =
      platform === "ios"
        ? app
        : app === "time"
        ? TIME_VARIANT_PACKAGE
        : DEFAULT_APP_PACKAGE;
    const handoffUrl = new URL(deepLinkPath, "https://deep-link.local");
    handoffUrl.searchParams.set(APP_HANDOFF_PARAM, app);

    try {
      const result = await AppLauncher.openUrl({
        url: `${scheme}://${handoffUrl.pathname}${handoffUrl.search}${handoffUrl.hash}`,
      });
      if (!result.completed) {
        message.error(
          `Unable to open ${app === "time" ? "Time" : "Transform"}.`,
        );
      }
    } catch (error) {
      console.error(`[DeepLinkingHandler] Failed to open ${app} app:`, error);
      message.error(`Unable to open ${app === "time" ? "Time" : "Transform"}.`);
    }
  };

  return (
    <>
      {showFallbackPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999999,
            padding: 20,
          }}
          onClick={() => setShowFallbackPopup(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 400,
              background: "#ffffff",
              borderRadius: 18,
              boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
              padding: 24,
              textAlign: "center",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="text-center mb-3">
              <img
                src={logo}
                width={250}
                height={125}
                className="img-fluid object-fit-contain"
                alt="Logo"
              />
            </div>
            <div className="fw-bold fs-4">Open the app?</div>
            <div className="fw-normal fs-6">
              It looks like you opened this page in a browser. Tap below to try
              opening the app directly.
            </div>
            <div
              style={{
                display: "flex",
                width: "100%",
                gap: 12,
                marginTop: 24,
              }}
            >
              {(["transform", "time"] as const).map((app) => (
                <button
                  key={app}
                  type="button"
                  onClick={() => handleOpenBrowserApp(app)}
                  aria-label={`Open link in ${
                    app === "time" ? "Time" : "Transform"
                  }`}
                  style={{
                    flex: 1,
                    minHeight: 112,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    background: "#ffffff",
                    color: "#231f20",
                    fontFamily: "'Nunito Sans Bold', sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  <span
                    className="deep-link-app-icon"
                    style={{
                      width: 52,
                      height: 52,
                      display: "grid",
                      placeItems: "center",
                      overflow: "hidden",
                      borderRadius: 12,
                      border:
                        app === "time"
                          ? "1px solid #000000"
                          : "1px solid #e5e5e5",
                      background: app === "time" ? "#000000" : "#ffffff",
                    }}
                  >
                    {app === "time" ? <TimeTabIcon /> : <TransformTabIcon />}
                  </span>
                  {app === "time" ? "Time" : "Transform"}
                </button>
              ))}
            </div>
            <button
              className="btn btn-outline-secondary mt-3 px-4"
              onClick={() => setShowFallbackPopup(false)}
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      )}
      <Drawer
        placement="bottom"
        closable={false}
        maskClosable
        onClose={() => setShowAppChooser(false)}
        open={showAppChooser}
        height="auto"
        zIndex={1000000}
        rootClassName="deep-link-app-chooser-root"
        styles={{
          mask: {
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(2px)",
            zIndex: 1000000,
          },
          wrapper: {
            zIndex: 1000000,
          },
          content: {
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            backgroundColor: "#ffffff",
            overflow: "hidden",
            maxWidth: "100%",
            margin: "0 auto",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            zIndex: 1000000,
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "28px 24px 32px",
            fontFamily: "'Nunito Sans Regular', sans-serif",
          }}
        >
          <h2
            style={{
              fontFamily: "'Nunito Sans Bold', sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "#000000",
              margin: "0 0 24px",
            }}
          >
            Open Link On
          </h2>
          <div
            style={{
              display: "flex",
              width: "100%",
              maxWidth: 420,
              gap: 16,
            }}
          >
            {(["transform", "time"] as const).map((app) => (
              <button
                key={app}
                type="button"
                onClick={() => void openInApp(app)}
                aria-label={`Open link in ${
                  app === "time" ? "Time" : "Transform"
                }`}
                style={{
                  flex: 1,
                  minHeight: 132,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  border: "1px solid #e5e5e5",
                  borderRadius: 16,
                  background: "#ffffff",
                  color: "#231f20",
                  fontFamily: "'Nunito Sans Bold', sans-serif",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 64,
                    height: 64,
                    display: "grid",
                    placeItems: "center",
                    overflow: "hidden",
                    borderRadius: 14,
                    border:
                      app === "time"
                        ? "1px solid #000000"
                        : "1px solid #e5e5e5",
                    background: app === "time" ? "#000000" : "#ffffff",
                  }}
                  className="deep-link-app-icon"
                >
                  {app === "time" ? <TimeTabIcon /> : <TransformTabIcon />}
                </span>
                {app === "time" ? "Time" : "Transform"}
              </button>
            ))}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default DeepLinkingHandler;
